import { isEmpty } from "lodash";
import React, { FC, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { useTerminatePoll } from "~/hooks";
import { PollResponseData } from "~/queries/chat/poll/useChatConversationPoll";
import { Button } from "~/ui/components";
import { Icon, LoadingIcon } from "~/ui/components/icons";
import { checkIsPollExpired } from "~/utils";

export const PollActions: FC<{
  chatId: number;
  isOwner: boolean;
  poll: PollResponseData;
  onEdit: () => void;
  onNewClick: () => void;
  isCreating: boolean;
  shouldNotShowAction: boolean;
}> = ({
  chatId,
  poll,
  isOwner = false,
  onEdit,
  onNewClick,
  isCreating,
  shouldNotShowAction = false,
}) => {
  const {
    handleDeletePoll,
    handleClosePoll,
    isDeletingPoll,
    isClosingPoll,
  } = useTerminatePoll(poll, chatId, shouldNotShowAction);

  const isPollExpired = useMemo(() => {
    if (!poll) return false;
    return checkIsPollExpired(poll.poll.created_at, poll.poll.expires_in_days);
  }, [poll]);

  return (
    <div className="flex flex-col pd-sm gap-sm">
      <div className="flex w-100 gap-sm justify-between">
        {(isOwner || !shouldNotShowAction) &&
          !isCreating &&
          poll &&
          !isEmpty(poll?.poll) &&
          poll?.poll.active &&
          !isPollExpired &&
          poll.yes === 0 &&
          poll?.no === 0 && (
            <Button
              onClick={onEdit}
              variant="success"
              size="sm"
              className="font-variant-small-caps flex items-center justify-between gap-md w-100"
            >
              <FormattedMessage defaultMessage="Edit poll" id="kVyCSg" />
              <Icon icon={["fal", "pen"]} color="primary" size="sm" />
            </Button>
          )}
        {(isOwner || !shouldNotShowAction) && !isCreating && (
          <Button
            onClick={onNewClick}
            variant="success"
            size="sm"
            className="font-variant-small-caps flex items-center justify-between gap-md w-100"
          >
            <FormattedMessage defaultMessage="New poll" id="vyKRp1" />
            <Icon icon={["fal", "pen"]} color="primary" size="sm" />
          </Button>
        )}
      </div>
      {(isOwner || !shouldNotShowAction) &&
        !isEmpty(poll?.poll) &&
        !isCreating && (
          <div className="flex w-100 gap-sm justify-between">
            {poll?.poll.active && (
              <Button
                onClick={handleClosePoll}
                variant="success"
                size="sm"
                className="font-variant-small-caps flex items-center justify-between gap-md w-100"
              >
                <FormattedMessage defaultMessage="Close poll" id="6UaVAT" />
                {isClosingPoll ? (
                  <LoadingIcon size="sm" />
                ) : (
                  <Icon
                    icon={["far", "check-square"]}
                    color="primary"
                    size="sm"
                  />
                )}
              </Button>
            )}
            <Button
              onClick={handleDeletePoll}
              variant="danger"
              size="sm"
              className="font-variant-small-caps flex items-center justify-between gap-md w-100"
            >
              <FormattedMessage defaultMessage="Delete poll" id="Br4H3b" />
              {isDeletingPoll ? (
                <LoadingIcon size="sm" />
              ) : (
                <Icon icon={["far", "trash"]} color="primary" size="sm" />
              )}
            </Button>
          </div>
        )}
    </div>
  );
};
